import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useState } from 'react';

import { Loader, Tabs } from '@thinknet-gmbh/platform-common-ui';

import { useGetEmployeeById } from 'src/hooks/employees/get-employee-by-id.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useRouteQuery } from 'src/common/helpers';

import { EmployeeDocuments } from './employee-details-tabs/employee-documents';
import { PersonalInfoView } from 'src/components/employee/personal-info-view';
import { EducationsAndSkillsView } from './employee-details-tabs/educations-and-skills/educations-and-skills';
import { PaymentAndInsuranceView } from 'src/components/employee/payment-and-insurance/payment-and-insurance-view';
import { WorkingConditionsList } from 'src/components/employee/working-conditions-list';

import { Processes } from './employee-details-tabs/processes';
import { PersonalInfoEdit } from './employee-details-tabs/personal-info/personal-info-edit';
import { WorkingConditionsEdit } from './employee-details-tabs/working-conditions/working-conditions-edit';
import { PaymentAndInsuranceEdit } from './employee-details-tabs/payment-and-insurance/payment-and-insurance-edit';
import { EducationsAndSkillsEdit } from './employee-details-tabs/educations-and-skills/educations-and-skills-edit';
import { WorkingConditionsCreate } from './employee-details-tabs/working-conditions/working-conditions-create';
import { EditProcess } from './employee-details-tabs/processes/components/edit-process';

import { EMPLOYEE_DETAILS_TABS } from './employee-details-tabs/employee-details.constants';

import { IProcess } from './employee-details-tabs/processes/processes.interfaces';

import { EmployeeDetailsWrapper } from './employee-details.styles';
import { WagesAndSalaryEdit } from './employee-details-tabs/wages-and-salary/wages-and-salary-edit';
import WagesAndSalaryView
  from './employee-details-tabs/wages-and-salary/wages-and-salary-view/wages-and-salary-view.component';


const { TabPane } = Tabs;

const EmployeeDetails = () => {
  const { t } = useTranslation(['settings', 'employee']);
  const location = useLocation();

  const [editPage, setEditPage] = useState<string | null>(null);
  const [editData, setEditData] = useState<any>(location.state?.initialData || null);
  const [createPage, setCreatePage] = useState<string | null>(
    location.state?.defaultCreatePage || null
  );

  const query = useRouteQuery();
  const _id: string = query.get('id') || '';

  const currentCompanyId = useGetCurrentCompanyId();
  const { employee, loading } = useGetEmployeeById(_id as string);

  const onCancel = () => {
    setEditPage(null);
    setEditData(null);
    setCreatePage(null);
  };

  if (loading || !currentCompanyId || !_id) {
    return <Loader />;
  }

  return (
    <EmployeeDetailsWrapper>
      <Tabs onChange={onCancel} defaultActiveKey={location.state?.activeTabKey}>
        <TabPane tab={t('employee:employeePersonalInfo.labels.personalInfo')} key="1">
          {editPage === EMPLOYEE_DETAILS_TABS.personalInfo ? (
            <PersonalInfoEdit onCancel={onCancel} initialValues={employee} />
          ) : (
            <PersonalInfoView
              employee={employee}
              onClickEdit={() => setEditPage(EMPLOYEE_DETAILS_TABS.personalInfo)}
            />
          )}
        </TabPane>
        <TabPane tab={t('employee:labels.workingConditions')} key="2">
          {createPage === EMPLOYEE_DETAILS_TABS.workingConditions ? (
            <WorkingConditionsCreate onCancel={onCancel} initialValues={editData} />
          ) : editPage === EMPLOYEE_DETAILS_TABS.workingConditions ? (
            <WorkingConditionsEdit onCancel={onCancel} initialValues={editData} />
          ) : (
            <WorkingConditionsList
              createdBy={employee.createdBy}
              onClickEdit={(data: any) => {
                setEditPage(EMPLOYEE_DETAILS_TABS.workingConditions);
                setEditData(data);
              }}
              onClickCreate={(contractType) => {
                setCreatePage(EMPLOYEE_DETAILS_TABS.workingConditions)
                setEditData({
                  contracts: [{
                    contractType
                  }]
                })
              }}
            />
          )}
        </TabPane>
        <TabPane tab={t('employee:wagesAndSalary.labels.wagesAndSalary')} key="3">
          {editPage === EMPLOYEE_DETAILS_TABS.wagesAndSalary ? (
            <WagesAndSalaryEdit currentCompanyId={currentCompanyId} onCancel={onCancel} />
          ) : (
            <WagesAndSalaryView
              onClickEdit={() => setEditPage(EMPLOYEE_DETAILS_TABS.wagesAndSalary)}
            />
          )}
        </TabPane>
        <TabPane tab={t('employee:labels.paymentAndInsurance')} key="4">
          {editPage === EMPLOYEE_DETAILS_TABS.paymentAndInsurance ? (
            <PaymentAndInsuranceEdit
              onCancel={onCancel}
              initialValues={employee.paymentAndInsurance}
            />
          ) : (
            <PaymentAndInsuranceView
              paymentAndInsurance={{
                ...employee.paymentAndInsurance,
                createdBy: employee.createdBy,
              }}
              healthInsurance={employee?.healthInsurance}
              onClickEdit={() => setEditPage(EMPLOYEE_DETAILS_TABS.paymentAndInsurance)}
            />
          )}
        </TabPane>
        <TabPane tab={t('settings:documents.labels.documents')} key="5">
          <EmployeeDocuments documentsIds={employee.documentsIds} createdBy={employee.createdBy} />
        </TabPane>
        <TabPane tab={t('employee:process.labels.processes')} key="6">
          {editPage === EMPLOYEE_DETAILS_TABS.processes ? (
            <EditProcess
              onCancel={onCancel}
              processData={editData}
            />
          ) : (
            <Processes
              employeeData={{
                memberId: employee._id,
              }}
              onClickEdit={(process: IProcess) => {
                setEditPage(EMPLOYEE_DETAILS_TABS.processes);
                setEditData(process);
              }}
            />
          )}
        </TabPane>
        <TabPane tab={t('employee:educationsAndSkills.labels.headerTitle')} key="7">
          {editPage === EMPLOYEE_DETAILS_TABS.educationsAndSkills ? (
            <EducationsAndSkillsEdit
              onCancel={onCancel}
              initialValues={employee.educationAndSkills}
            />
          ) : (
            <EducationsAndSkillsView
              educationsAndSkills={{
                ...employee.educationAndSkills,
                createdBy: employee.createdBy,
              }}
              onClickEdit={() => setEditPage(EMPLOYEE_DETAILS_TABS.educationsAndSkills)}
            />
          )}
        </TabPane>
      </Tabs>
    </EmployeeDetailsWrapper>
  );
};

export default EmployeeDetails;
